import * as React from 'react';

import { OfferPreviewContainer } from '../OfferPreview';
import { ControlsContainer } from './Controls';
import { Tags } from './Tags';
import { useBoundsSync } from './hooks/useBoundsSync';
import { useCloseOfferPreviewOnMapClick } from './hooks/useCloseOfferPreviewOnMapClick';
import { useDisablePinDblClickZoom } from './hooks/useDisablePinDblClickZoom';
import { useGeoFocusRefetch } from './hooks/useGeoFocusRefetch';
import { useInfrastructure } from './hooks/useInfrastructure';
import { useListingAreaReservation } from './hooks/useListingAreaReservation';
import { useListingRestrictMapArea } from './hooks/useListingRestrictMapArea';
import { useMapViewportFit } from './hooks/useMapViewportFit';
import { useOfferHoverPin } from './hooks/useOfferHoverPin';
import { usePinHover } from './hooks/usePinHover';
import { usePinsClickHandlers } from './hooks/usePinsClickHandlers';
import { usePinsDataSync } from './hooks/usePinsDataSync';
import { usePinsDetailsVisibility } from './hooks/usePinsDetailsVisibility';
import { usePinsFetching } from './hooks/usePinsFetching';
import { usePinsLayer } from './hooks/usePinsLayer';
import { usePinsOffersCounter } from './hooks/usePinsOffersCounter';
import { usePinsResultability } from './hooks/usePinsResultability';
import { usePinsTestMode } from './hooks/usePinsTestMode';
import { usePolygons } from './hooks/usePolygons';

interface IMapContentProps {
  ymaps: YMaps.IYMaps;
  map: YMaps.Map;
  showTags?: boolean;
  isRedesign: boolean;
}

export function MapContent({ ymaps, map, showTags, isRedesign }: IMapContentProps) {
  const pinsLayer = usePinsLayer({ ymaps, map, isRedesign });
  const isDetailsVisible = usePinsDetailsVisibility(pinsLayer);
  const isResultsAvailable = usePinsResultability(pinsLayer);

  const onFocusApplied = useGeoFocusRefetch();

  // Резерв полосы под desktop listing идёт ДО useBoundsSync: на маунте margin должен быть
  // установлен раньше первого чтения bounds, иначе первый запрос пинов уйдёт по полной карте,
  // а не по видимой части (см. useBoundsSync - getBounds с useMapMargin).
  const listingSafeAreaWidth = useListingAreaReservation({ rbush: pinsLayer.rbush });
  useListingRestrictMapArea(map, listingSafeAreaWidth);
  useMapViewportFit(map);
  useCloseOfferPreviewOnMapClick(map);
  useBoundsSync({ map, onFocusApplied });
  usePinsFetching();
  usePinsClickHandlers({ ...pinsLayer, isResultsAvailable, isDetailsVisible });
  useDisablePinDblClickZoom({ map, balloonManager: pinsLayer.balloonManager });
  useOfferHoverPin({ ...pinsLayer, isDetailsVisible });
  usePinHover({ balloonManager: pinsLayer.balloonManager });
  usePinsDataSync({ ...pinsLayer, map, isDetailsVisible });
  usePinsTestMode({ ...pinsLayer, map });
  usePinsOffersCounter(pinsLayer);
  usePolygons({ ymaps, map });
  useInfrastructure({ ymaps, map });

  return (
    <>
      {showTags && <Tags />}
      <ControlsContainer ymaps={ymaps} map={map} />
      <OfferPreviewContainer ymaps={ymaps} map={map} listingSafeAreaWidth={listingSafeAreaWidth} />
    </>
  );
}
